/**
 * 태그 에디터 유틸리티 함수
 * @description 템플릿 문자열(#{...})과 Tiptap JSON 콘텐츠 간 양방향 변환을 담당한다.
 */

import type { JSONContent } from "@tiptap/core";
import type { PlaceholderConfig } from "../../types/sms.types";

/** #{변수명} 패턴 매칭 */
const PLACEHOLDER_REGEX = /#\{([^}]+)\}/g;

/**
 * 템플릿 문자열을 Tiptap JSON 콘텐츠로 변환
 */
export const parseTemplateToContent = (
  template: string,
  placeholders: PlaceholderConfig[],
): JSONContent => {
  if (!template) {
    return {
      type: "doc",
      content: [{ type: "paragraph", content: [] }],
    };
  }

  const placeholderMap = new Map(placeholders.map((p) => [p.placeholder, p]));
  const lines = template.split("\n");

  const paragraphs: JSONContent[] = lines.map((line) => {
    const content: JSONContent[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    const regex = new RegExp(PLACEHOLDER_REGEX.source, "g");

    while ((match = regex.exec(line)) !== null) {
      if (match.index > lastIndex) {
        const text = line.slice(lastIndex, match.index);
        if (text) {
          content.push({ type: "text", text });
        }
      }

      const fullPlaceholder = match[0];
      const config = placeholderMap.get(fullPlaceholder);

      if (config) {
        content.push({
          type: "placeholderTag",
          attrs: {
            placeholder: config.placeholder,
            label: config.label,
            color: config.color,
          },
        });
      } else {
        // 알 수 없는 플레이스홀더: 일반 텍스트로 표시
        content.push({ type: "text", text: fullPlaceholder });
      }

      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < line.length) {
      const text = line.slice(lastIndex);
      if (text) {
        content.push({ type: "text", text });
      }
    }

    return {
      type: "paragraph",
      content: content.length > 0 ? content : undefined,
    };
  });

  return {
    type: "doc",
    content: paragraphs,
  };
};

/**
 * Tiptap JSON 콘텐츠를 템플릿 문자열로 변환
 */
export const serializeContentToTemplate = (json: JSONContent): string => {
  if (!json.content) {
    return "";
  }

  const lines = json.content.map((paragraph) => {
    if (!paragraph.content) {
      return "";
    }

    return paragraph.content
      .map((node) => {
        switch (node.type) {
          case "text":
            return node.text || "";
          case "placeholderTag":
            return node.attrs?.placeholder || "";
          case "hardBreak":
            return "\n";
          default:
            return "";
        }
      })
      .join("");
  });

  return lines.join("\n");
};

/**
 * 템플릿 내 플레이스홀더 추출 (중복 제거)
 */
export const extractPlaceholders = (template: string): string[] => {
  const matches = template.match(PLACEHOLDER_REGEX);
  return matches ? [...new Set(matches)] : [];
};
